import React, { FC, useEffect, useState } from 'react';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Grid,
} from '@mui/material';

import { BackflowJobItem } from '../../model';

import axios from '../../../sharedModules/utils/axios.utils';

import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';
import LoadingWrapper from '../../../sharedModules/layout/LoadingWrapper';

interface JobDetailsToCopyModalProps {
  item: BackflowJobItem;
  modalOpen: boolean;
  setModalOpen: (open: boolean) => void;
}

const JobDetailsToCopyModal: FC<JobDetailsToCopyModalProps> = (props) => {
  const { item, modalOpen, setModalOpen } = props;

  const [categories, setCategories] = useState<string[]>([]);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [detailsFetched, setDetailsFetched] = useState<boolean>(false);
  const [jobDetails, setJobDetails] = useState<any>({});
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const handleCopyField = (value: string, fieldName: string) => {
    navigator.clipboard.writeText(value);
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const fetchJobDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.get<any>(`/backflows/water_authority_submissions/${item.id}`);
      setJobDetails(response.data.result);
      setCategories(response.data.categories);
    } catch (error) {
      setErrorMessage('Something went wrong fetching the job details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (modalOpen && !detailsFetched) {
      fetchJobDetails();
      setDetailsFetched(true);
    }
  }, [modalOpen, detailsFetched]);

  const DetailRow: FC<{ label: string; value: string | undefined; fieldKey: string }> = ({
    label,
    value,
    fieldKey,
  }) => {
    const displayValue = value || 'N/A';
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1.5,
          px: 2,
          borderRadius: 1,
          bgcolor: 'grey.50',
          '&:hover': { bgcolor: 'grey.100' },
          mb: 1,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase' }}>
            {label}
          </Typography>
          <Typography
            variant="body1"
            fontWeight={500}
            sx={{
              mt: 0.5,
              fontFamily: 'monospace',
              userSelect: 'all',
              cursor: 'text',
            }}
          >
            {displayValue}
          </Typography>
        </Box>
        {value && (
          <IconButton
            size="small"
            onClick={() => handleCopyField(value, fieldKey)}
            sx={{
              ml: 2,
              transition: 'all 0.2s',
              color: copiedField === fieldKey ? 'success.main' : 'action.active',
            }}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    );
  };

  return (
    <Dialog
      open={modalOpen}
      onClose={() => setModalOpen(false)}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 2,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={600}>
            Job Details - {item.asset_no}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            Click field values to select, use copy buttons to copy to clipboard
          </Typography>
        </Box>
        <IconButton
          onClick={() => setModalOpen(false)}
          sx={{
            color: 'white',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <LoadingWrapper isLoading={loading}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <ErrorMessage message={errorMessage} />
            </Grid>
            {categories?.map((category) => {
              const entries = Object.entries(jobDetails || {}).filter(
                ([_, v]: [string, any]) => v.category === category,
              );

              if (entries.length === 0) return null;

              return (
                <Grid item xs={12} key={category}>
                  <Typography variant="h6" color="primary" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
                    {category}
                  </Typography>

                  {entries.map(([key, item]: [string, any]) => (
                    <DetailRow key={key} label={item.label} value={item.value} fieldKey={key} />
                  ))}
                </Grid>
              );
            })}
          </Grid>
        </LoadingWrapper>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, bgcolor: 'grey.50' }}>
        <Button onClick={() => setModalOpen(false)} variant="contained" size="large">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default JobDetailsToCopyModal;
